import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot,
  Loader2,
  Crown,
  Sparkles,
  Volume2,
  UserMinus,
  Radio,
  Zap,
  BookOpen,
} from 'lucide-react';
import { useQueryClient } from '../lib/queryClient';
import {
  useSessionAiParticipants,
  AiParticipantTypingStatus,
  useRemoveAiParticipant,
} from '../helpers/useSessionAiParticipants';
import { getCommandInfo, parseSlashCommand } from '../helpers/slashCommands';
import { useAiChatHistory, useSendAiMessage } from '../helpers/useAiChat';
import { usePresenceCentralized } from '../helpers/usePresenceApi';
import { useRealtimeSession } from '../helpers/useRealtimeSession';
import { useAuth } from '../helpers/useAuth';
import { AiChatMessage } from './AiChatMessage';
import { AiChatInput } from './AiChatInput';
import { ModelSelection } from './ModelSelector';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from './ContextMenu';
import { AiVoiceControls, useAiVoiceMuted } from './AiVoiceControls';
import { getVoiceForPersona, cleanTextForTTS } from '../helpers/useInworldTTS';
import { useTTS } from '../helpers/useTTS';
import { toast } from 'sonner';
import { CommandListModal } from './CommandListModal';
import { LiveKitMeeting } from './LiveKitMeeting';
import { Button } from './Button';
import styles from './CombinedChatPanel.module.css';

// Simple string hash for consistent numeric seed from personaId
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Generate unique robot avatar URL using RoboHash
const getRobotAvatar = (seed: string | number): string => {
  const numericSeed = typeof seed === 'string' ? hashString(seed) : seed;
  return `https://robohash.org/${numericSeed}.png?set=set1&size=64x64`;
};

interface CombinedChatPanelProps {
  sessionId: string;
  isOwner?: boolean;
  className?: string;
  currentFile?: string;
  onOpenCoding?: () => void;
  onOpenSkillSearch?: (query?: string) => void;
  onOpenSkillTree?: (mode?: 'view' | 'create') => void;
  activeSkills?: { name: string; description: string; content?: string }[];
}

export const CombinedChatPanel: React.FC<CombinedChatPanelProps> = ({
  sessionId,
  isOwner,
  className,
  currentFile,
  onOpenCoding,
  onOpenSkillSearch,
  onOpenSkillTree,
  activeSkills = [],
}) => {
  const queryClient = useQueryClient();
  const [isCmdListOpen, setIsCmdListOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef<boolean>(true);
  const scrollRafRef = useRef<number>(0);
  const { authState } = useAuth();
  const currentUser = authState.type === 'authenticated' ? authState.user : null;

  // AI participants
  const { data: aiParticipants, isLoading: loadingParticipants } =
    useSessionAiParticipants(sessionId);
  const activeParticipants = aiParticipants?.filter((p) => p.isActive) || [];
  // Each participant is unique — count individually, never collapse by quantity
  const totalParticipantCount = activeParticipants.length;
  const removeParticipant = useRemoveAiParticipant();

  // Handler to remove AI participant via right-click menu
  const handleRemoveParticipant = async (
    participantId: string | undefined,
    personaName: string,
  ) => {
    if (!sessionId || !participantId) {
      toast.error('Session or participant ID is missing. Please try again.');
      return;
    }

    try {
      await removeParticipant.mutateAsync({ sessionId, participantId });
      toast.success(`${personaName} has left the collaboration.`);
    } catch (error) {
      // Surface server message when available for easier debugging
      const errMsg = error instanceof Error ? error.message : 'Failed to remove participant';
      console.error('[removeParticipant] error', errMsg, { sessionId, participantId });
      toast.error(errMsg);
    }
  };

  // AI chat history
  const { data: history, isLoading: loadingHistory } = useAiChatHistory(sessionId);
  const { data: presenceData } = usePresenceCentralized(sessionId);

  // Track typing AI participants
  const [typingParticipants, setTypingParticipants] = useState<
    Map<number, AiParticipantTypingStatus>
  >(new Map());

  // Voice TTS for AI participants — with speech queue for sequential playback
  const isMuted = useAiVoiceMuted();
  const { speak, stop: stopSpeaking, isPlaying: isSpeaking } = useTTS();
  const spokenIdsRef = useRef<Set<string>>(new Set());
  const [speakingPersonaId, setSpeakingPersonaId] = useState<number | null>(null);
  const speechQueueRef = useRef<Array<{ text: string; voiceId: any; personaId: any }>>([]);
  const speechProcessingRef = useRef(false);

  // Process speech queue — plays messages one at a time
  const processSpeechQueue = useCallback(async () => {
    if (speechProcessingRef.current) return;
    speechProcessingRef.current = true;
    while (speechQueueRef.current.length > 0) {
      const item = speechQueueRef.current.shift()!;
      setSpeakingPersonaId(item.personaId);
      try {
        await speak(item.text, { voiceId: item.voiceId });
      } catch {}
      setSpeakingPersonaId(null);
    }
    speechProcessingRef.current = false;
  }, [speak]);

  // Real-time updates via WebSocket
  const { lastMessage } = useRealtimeSession(sessionId);

  // Handle real-time messages
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = typeof lastMessage === 'string' ? JSON.parse(lastMessage) : lastMessage;

      // AI participant message received - refresh history and auto-speak
      if (data.type === 'ai_chat_message') {
        queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
        // Stable auto-scroll: use rAF to wait for DOM update, then scroll smoothly
        if (isAtBottomRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
          scrollRafRef.current = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            });
          });
        }

        // Queue the message for TTS — plays sequentially so you hear every persona
        if (!isMuted && data.personaId && data.content) {
          const messageId = `${data.personaId}-${data.timestamp}`;
          if (!spokenIdsRef.current.has(messageId)) {
            spokenIdsRef.current.add(messageId);
            // Limit set size to prevent memory leak
            if (spokenIdsRef.current.size > 100) {
              const first = spokenIdsRef.current.values().next().value;
              if (first) spokenIdsRef.current.delete(first);
            }
            const cleanedText = cleanTextForTTS(data.content);
            if (cleanedText.length > 10) {
              const voiceId = getVoiceForPersona(data.personaId, data.personaName);
              speechQueueRef.current.push({ text: cleanedText, voiceId, personaId: data.personaId });
              processSpeechQueue();
            }
          }
        }
      }

      // AI participant typing status
      if (data.type === 'ai_typing' && data.personaId) {
        setTypingParticipants((prev) => {
          const next = new Map(prev);
          if (data.isTyping) {
            next.set(data.personaId, {
              personaId: data.personaId,
              isTyping: true,
              action: data.action || 'thinking',
            });
          } else {
            next.delete(data.personaId);
          }
          return next;
        });
      }

      // File changes - could indicate AI participant code generation
      if (data.type === 'file_changed' || data.type === 'file_created') {
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, [lastMessage, queryClient, sessionId]);

  // Model selection — check session-specific, then global preference, then default
  const [selectedModel, setSelectedModel] = useState<ModelSelection | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      // 1. Session-specific saved model
      const saved = localStorage.getItem(`ai-model-selection-${sessionId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.provider === 'string' && typeof parsed.model === 'string') {
          return parsed as ModelSelection;
        }
      }
      // 2. Global user preference (last model used in any session)
      const global = localStorage.getItem('ai-model-selection-global');
      if (global) {
        const parsed = JSON.parse(global);
        if (parsed && typeof parsed.provider === 'string' && typeof parsed.model === 'string') {
          return parsed as ModelSelection;
        }
      }
    } catch (e) {
      console.error('Failed to load model selection:', e);
    }
    return { provider: 'ollama_cloud', model: 'glm-5.1', isLocal: false };
  });

  // Save model selection to localStorage when it changes
  useEffect(() => {
    if (selectedModel && typeof window !== 'undefined') {
      try {
        const json = JSON.stringify(selectedModel);
        localStorage.setItem(`ai-model-selection-${sessionId}`, json);
        localStorage.setItem('ai-model-selection-global', json);
      } catch {}
    }
  }, [selectedModel, sessionId]);

  const [mode, setMode] = useState<'generate' | 'chat'>('generate');
  const [meetingActive, setMeetingActive] = useState(false);
  const [meetingChatMessage, setMeetingChatMessage] = useState<string>('');
  const autoExecuteValue = mode === 'generate';

  const { sendMessage, streamingMessage, stopGeneration } = useSendAiMessage(
    sessionId,
    autoExecuteValue,
    mode,
    undefined,
    mode === 'generate', // autoLoop: enable continuous execution in generate mode
  );

  // Track scroll position
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    isAtBottomRef.current = atBottom;
  };

  // Stable auto-scroll: uses rAF double-buffering to wait for DOM paint before scrolling.
  // This prevents the "jumping" caused by setTimeout racing with React re-renders.
  useEffect(() => {
    if (!isAtBottomRef.current) return;

    cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Use non-animated scroll during realtime updates to avoid accumulating jitter.
        scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      });
    });

    return () => cancelAnimationFrame(scrollRafRef.current);
  }, [history?.messages?.length, streamingMessage?.content, streamingMessage?.id]);

  // Keep bottom lock when content grows after render (e.g. streaming or dynamic blocks).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getUserStatus = (userId: number): 'online' | 'meeting' | 'away' | 'offline' | null => {
    const participant = presenceData?.find((p) => p.userId === userId);
    if (!participant && currentUser?.id === userId) return 'online';
    return participant?.status ?? null;
  };

  const getUserAvatarUrl = (userId: number): string | null => {
    const participant = presenceData?.find((p) => p.userId === userId);
    if (currentUser?.id === userId) {
      return participant?.avatarUrl ?? currentUser?.avatarUrl ?? null;
    }
    return participant?.avatarUrl ?? null;
  };

  // Build personaId → avatar URL map so message avatars match the participants bar
  const participantAvatarMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of activeParticipants) {
      map[p.personaId] = p.avatarUrl || getRobotAvatar(p.personaId);
    }
    return map;
  }, [activeParticipants]);

  // Handle sending message - AI participants respond organically
  const handleSend = async (
    message: string,
    context: string[],
    attachment?: { content: string; name: string },
  ) => {
    // ── Operator interrupt: immediately silence all AI voices ──
    stopSpeaking();
    speechQueueRef.current = [];
    speechProcessingRef.current = false;
    setSpeakingPersonaId(null);

    // If meeting is active, inject chat message into meeting conversation
    if (meetingActive && message.trim()) {
      setMeetingChatMessage(message.trim());
    }
    // Force scroll to bottom when user sends their own message
    isAtBottomRef.current = true;
    requestAnimationFrame(() => {
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });

    // Intercept slash commands
    const trimmed = message.trim();
    if (trimmed.startsWith('/')) {
      const parsed = parseSlashCommand(trimmed);
      if (parsed) {
        const cmdInfo = getCommandInfo(parsed.command);
        if (cmdInfo) {
          // Normalize aliases to canonical command definitions.
          const canonical = cmdInfo.command;
          if (canonical === '/cmdlist') {
            setIsCmdListOpen(true);
            return;
          }
          if (canonical === '/skills' || canonical === '/find') {
            if (onOpenSkillSearch) {
              onOpenSkillSearch(parsed.args || undefined);
            } else {
              toast.info('Skill search is not available in this view.');
            }
            return;
          }
          if (canonical === '/skilltree') {
            if (onOpenSkillTree) {
              onOpenSkillTree('view');
            } else {
              toast.info('Skill tree is not available in this view.');
            }
            return;
          }
          if (canonical === '/create-pack') {
            if (onOpenSkillTree) {
              onOpenSkillTree('create');
            } else {
              toast.info('Skill tree is not available in this view.');
            }
            return;
          }
          if (canonical === '/skill-list' || canonical === '/use-skill') {
            if (onOpenSkillSearch) {
              onOpenSkillSearch(parsed.args || undefined);
            } else {
              toast.info('Skill search is not available in this view.');
            }
            return;
          }
        }
        // Catch-all for other builtin commands — redirect to AI tab
        if (cmdInfo && cmdInfo.action === 'builtin') {
          toast.info(
            `Use the AI tab for /${parsed.command.replace('/', '')} — switch to the AI tab to use this command.`,
          );
          return;
        }
      }
    }

    // Build skills payload to pass via dedicated field
    const skillsPayload =
      activeSkills.length > 0
        ? activeSkills.map((s) => ({
            name: s.name,
            content: s.content,
            description: s.description,
          }))
        : undefined;

    // When AI participants are active, they'll respond automatically (organic!)
    // The backend handles triggering their responses
    await sendMessage(
      message,
      context,
      selectedModel
        ? {
            useLocalLlm: selectedModel.isLocal,
            provider: selectedModel.isLocal
              ? 'ollama_local'
              : selectedModel.provider || 'ollama_cloud',
            model: selectedModel.model,
            mode: mode,
            // agentLoop enables multi-turn collaboration where AI participants respond to each other
            agentLoop: activeParticipants.length > 0,
          }
        : { mode: mode, agentLoop: activeParticipants.length > 0 },
      attachment,
      undefined, // webSearchResults
      skillsPayload,
    );
  };

  const allMessages = (() => {
    const merged = [...(history?.messages || []), ...(streamingMessage ? [streamingMessage] : [])];
    // Deduplicate by msg.id to prevent x2 stacking (streaming + persisted)
    const seen = new Map<string, (typeof merged)[0]>();
    for (const msg of merged) {
      if (msg.id) {
        seen.set(msg.id, msg); // last occurrence wins (streaming overrides persisted)
      } else {
        seen.set(`_no_id_${seen.size}`, msg);
      }
    }
    return Array.from(seen.values());
  })();

  // Get typing participant names for indicator
  const typingNames = Array.from(typingParticipants.values())
    .map((tp) => {
      const participant = activeParticipants.find((p) => p.personaId === tp.personaId);
      return participant?.personaName || 'AI';
    })
    .filter(Boolean);

  return (
    <div className={`${styles.container} ${className || ''}`}>
          <div className={styles.aiCollabPanel}>
            {/* Active AI Participants Bar - shows who's in the collaboration */}
            {activeParticipants.length > 0 && (
              <div className={styles.participantsBar}>
                <div className={styles.participantsLabel}>
                  <Sparkles size={14} />
                  <span>AI Team</span>
                </div>
                <div className={styles.participantsList}>
                  {/* Owner/Lead indicator */}
                  {isOwner && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={styles.ownerIndicator}>
                          <Crown size={14} />
                          <span>Lead</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className={styles.tooltipContent}>
                          <strong>You are the Lead</strong>
                          <span className={styles.category}>
                            AI participants follow your direction
                          </span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {activeParticipants.map((participant) => {
                    const isTyping = typingParticipants.has(participant.personaId);
                    const isSpeakingNow = speakingPersonaId === participant.personaId;
                    // Use personaId only for consistent avatar across participant bar and messages
                    const robotAvatar = getRobotAvatar(participant.personaId);
                    const uniqueKey = `participant-${participant.id}`;
                    return (
                      <Tooltip key={uniqueKey}>
                        <ContextMenu>
                          <ContextMenuTrigger asChild>
                            <TooltipTrigger asChild>
                              <div
                                className={`${styles.participantAvatar} ${isTyping ? styles.typing : ''} ${isSpeakingNow ? styles.speaking : ''}`}
                              >
                                <Avatar className={styles.avatar}>
                                  <AvatarImage
                                    src={participant.avatarUrl || robotAvatar}
                                    alt={participant.personaName}
                                  />
                                  <AvatarFallback className={styles.aiFallback}>
                                    <Bot size={12} />
                                  </AvatarFallback>
                                </Avatar>
                                <div
                                  className={`${styles.aiIndicator} ${isTyping ? styles.typingIndicator : ''} ${isSpeakingNow ? styles.speakingIndicator : ''}`}
                                />
                                {/* Each participant shown individually — no quantity stacking */}
                                {isSpeakingNow && (
                                  <div className={styles.voiceWaves}>
                                    <Volume2 size={10} />
                                  </div>
                                )}
                              </div>
                            </TooltipTrigger>
                          </ContextMenuTrigger>
                          <ContextMenuContent>
                            <ContextMenuItem
                              onClick={() =>
                                handleRemoveParticipant(
                                  String(participant.id),
                                  participant.personaName,
                                )
                              }
                              className={styles.removeMenuItem}
                            >
                              <UserMinus size={14} />
                              <span>Remove {participant.personaName}</span>
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                        <TooltipContent>
                          <div className={styles.tooltipContent}>
                            <strong>{participant.displayName || participant.personaName}</strong>
                            <span className={styles.category}>{participant.personaCategory}</span>
                            {isTyping && (
                              <span className={styles.typingStatus}>
                                {typingParticipants.get(participant.personaId)?.action ||
                                  'thinking'}
                                ...
                              </span>
                            )}
                            {isSpeakingNow && (
                              <span className={styles.speakingStatus}>Speaking...</span>
                            )}
                            <span className={styles.rightClickHint}>Right-click to remove</span>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
                {/* Voice Controls */}
                <AiVoiceControls className={styles.voiceControls} />
                <LiveKitMeeting sessionId={sessionId} disabled={false} userId={String(currentUser?.id || '')} userName={currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'} userAvatar={currentUser?.profileImage || undefined} onMeetingStart={() => setMeetingActive(true)} onMeetingStop={() => setMeetingActive(false)} chatMessage={meetingChatMessage} onChatMessageConsumed={() => setMeetingChatMessage('')} />
                {onOpenSkillSearch && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon-sm" onClick={() => onOpenSkillSearch()}>
                        <BookOpen size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>ClawdHub & OpenClaw Skills</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Agent Collaboration Toolbar — always visible */}
            {activeParticipants.length === 0 && (
              <div className={styles.participantsBar}>
                <div className={styles.participantsLabel}>
                  <Bot size={14} />
                  <span>AI Chat</span>
                </div>
                <div className={styles.participantsList}>
                  <LiveKitMeeting sessionId={sessionId} disabled={false} userId={String(currentUser?.id || '')} userName={currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'} userAvatar={currentUser?.profileImage || undefined} onMeetingStart={() => setMeetingActive(true)} onMeetingStop={() => setMeetingActive(false)} chatMessage={meetingChatMessage} onChatMessageConsumed={() => setMeetingChatMessage('')} />
                  {onOpenSkillSearch && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" onClick={() => onOpenSkillSearch()}>
                          <BookOpen size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>ClawdHub & OpenClaw Skills</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}

            {/* Typing Indicator */}
            {typingNames.length > 0 && (
              <div className={styles.typingBanner}>
                <Loader2 size={12} className={styles.spinner} />
                <span>
                  {typingNames.length === 1
                    ? `${typingNames[0]} is thinking...`
                    : `${typingNames.slice(0, -1).join(', ')} and ${typingNames.slice(-1)} are collaborating...`}
                </span>
              </div>
            )}

            {/* Messages Area */}
            <div className={styles.messagesArea} ref={scrollRef} onScroll={handleScroll}>
              {loadingHistory ? (
                <div className={styles.loadingState}>
                  <Loader2 size={24} className={styles.spinner} />
                  <span>Loading conversation...</span>
                </div>
              ) : allMessages.length === 0 ? (
                <div className={styles.emptyState}>
                  <Bot size={48} className={styles.emptyIcon} />
                  <p>AI Collaboration</p>
                  <p className={styles.subtext}>
                    {totalParticipantCount > 0
                      ? `Start chatting! ${totalParticipantCount} AI participant${totalParticipantCount > 1 ? 's' : ''} will join the conversation naturally.`
                      : 'Add AI participants from the session settings to enable team collaboration.'}
                  </p>
                </div>
              ) : (
                allMessages.map((msg, idx) => {
                  const isStreamingMsg = msg.id === streamingMessage?.id;

                  return (
                    <AiChatMessage
                      key={msg.id || `msg-${idx}`}
                      message={msg}
                      sessionId={sessionId}
                      isStreaming={isStreamingMsg}
                      userStatus={
                        msg.role === 'user' && 'userId' in msg
                          ? getUserStatus(msg.userId)
                          : undefined
                      }
                      userAvatarUrlFallback={
                        msg.role === 'user' && 'userId' in msg
                          ? getUserAvatarUrl(msg.userId)
                          : undefined
                      }
                      currentFile={currentFile}
                      currentUserId={currentUser?.id}
                      currentUserDisplayName={currentUser?.displayName ?? null}
                      participantAvatarMap={participantAvatarMap}
                    />
                  );
                })
              )}
              {/* Invisible scroll anchor — rAF scrolls to this for stable positioning */}
              <div ref={scrollAnchorRef} style={{ height: 1, width: '100%' }} />
            </div>

            {/* Input Area */}
            <div className={styles.inputArea}>
              <AiChatInput
                onSend={handleSend}
                disabled={false}
                currentFile={currentFile}
                sessionId={sessionId}
                onStopGeneration={streamingMessage ? stopGeneration : undefined}
                mode={mode}
                onModeChange={setMode}
                autoExecute={autoExecuteValue}
                onAutoExecuteChange={() => {}}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                isOwner={isOwner}
                autoStartVoice={meetingActive}
                onVoiceInterrupt={() => {
                  stopSpeaking();
                  speechQueueRef.current = [];
                  speechProcessingRef.current = false;
                  setSpeakingPersonaId(null);
                }}
              />
            </div>
          </div>
      <CommandListModal open={isCmdListOpen} onOpenChange={setIsCmdListOpen} />
    </div>
  );
};
